// Commercial Accounts — one page with tabs. Old /terms URLs redirect here (see legacyTermsPath).
import { useEffect, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useTermsScope } from "@/lib/termsAccess";
import { StepIndicator, ApprovedBadge } from "@/components/terms/StepIndicator";
import { TermsSettingsPanel } from "@/components/terms/TermsSettingsPanel";
import TermsRegister from "@/pages/dashboard/TermsRegister";
import TermsLegacy from "@/pages/dashboard/TermsLegacy";

export const TABS = [["how", "How it works"], ["new", "New applications"], ["legacy", "Legacy list"], ["active", "Active terms"], ["completed", "Completed"], ["settings", "Settings"]];
export const FILTERS = [["all", "All"], ["kat", "Waiting on Kat"], ["approval", "Waiting on approval"], ["approved", "Approved"], ["denied", "Denied"]];
export const HOW_STEPS = [
  ["Applied", null, "Applicant submits online; the system records it."],
  ["Eligibility", "Kat", "Checks two cash projects in the last 180 days."],
  ["Review", "Kat", "Checks the application, bank and three trade references."],
  ["Recommendation", "Kat", "Records approve, deny (with a reason code) or not yet eligible."],
  ["Second approval", "Brett or Nathanael", "Records the decision."],
  ["Decision email", "Kat", "Sends the approved or denied email after Brett approves its wording."],
  ["Agreement signed", "Customer", "Signs the terms agreement."],
  ["Terms active", "Kat", "Sets the term in ServiceTitan and confirms to the customer."],
];
export const POLICY = "Under $4,000 paid in full before service; $4,000+ is 50% before work; approved terms are 50% before work with the balance due in 15 days (30 for Net 30).";

export function legacyTermsPath(slug, rest) {
  const base = `/${slug}/app/commercial-accounts`;
  if (!rest) return `${base}/new`;
  if (rest === "register") return `${base}/active`;
  if (rest === "legacy") return `${base}/legacy`;
  return `${base}/new/${rest}`;
}

export function TermsRedirect() {
  const { slug, appId } = useParams();
  const { pathname } = useLocation();
  const rest = appId || (pathname.endsWith("/terms/register") ? "register" : pathname.endsWith("/terms/legacy") ? "legacy" : "");
  return <Navigate to={legacyTermsPath(slug, rest)} replace />;
}

export const isActiveTerm = (r) => r.cohort === "2027" && !["voided", "ended"].includes(r.status);

export function summarize(apps, register) {
  const nonVoided = apps.filter((a) => a.status !== "voided");
  const p = nonVoided.map((a) => a.progress || {});
  return {
    newCount: p.filter((x) => !x.decided).length,
    awaiting: p.filter((x) => x.waiting_on === "approval").length,
    waitingKat: p.filter((x) => x.waiting_on === "kat").length,
    active: register.filter(isActiveTerm).length,
    legacy: register.filter((r) => r.cohort === "2026").length,
    completed: p.filter((x) => x.completed).length,
  };
}

export function filterApps(apps, f) {
  const open = apps.filter((a) => !a.progress?.completed);
  if (f === "kat") return open.filter((a) => a.progress?.waiting_on === "kat");
  if (f === "approval") return open.filter((a) => a.progress?.waiting_on === "approval");
  if (f === "approved") return open.filter((a) => a.progress?.approved);
  if (f === "denied") return apps.filter((a) => ["denied", "not_yet_eligible"].includes(a.progress?.end_state));
  return open;
}

export const landingTab = (s) => (s.waitingKat + s.awaiting > 0 ? "new" : "how");

function Summary({ s }) {
  const items = [["New applications", s.newCount, "new"], ["Awaiting second approval", s.awaiting, "awaiting"], ["Active terms", s.active, "active"], ["Legacy accounts", s.legacy, "legacy"]];
  return (
    <div className="flex gap-3 flex-wrap" data-testid="ca-summary">
      {items.map(([l, n, k]) => <div key={k} className="bg-white border border-[#e5e7eb] rounded-lg px-4 py-2" data-testid={`ca-summary-${k}`}><div className="text-xl font-semibold">{n}</div><div className="text-xs text-[#6b7280]">{l}</div></div>)}
    </div>
  );
}

function AppRow({ a, slug }) {
  const ap = a.progress?.approved;
  const isVoided = a.status === "voided";
  return (
    <tr className="border-t border-[#f1f2f4] align-top" data-testid={`ca-app-row-${a.id}`}>
      <td className="px-3 py-2"><Link className="text-[#0072bc] font-medium" to={`/${slug}/app/commercial-accounts/new/${a.id}`}>{a.company_name}</Link>
        <div className="text-xs text-[#6b7280]">Requested Net {a.requested_terms} · {(a.submitted_at || "").slice(0, 10)}</div></td>
      <td className="px-3 py-2"><StepIndicator progress={a.progress} compact testId={`ca-steps-${a.id}`} /></td>
      <td className="px-3 py-2">
        {isVoided && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-[#f5f6f8] text-[#6b7280] border border-[#e0e3ea]" data-testid={`ca-voided-badge-${a.id}`}>Voided</span>}
        {!isVoided && ap && <ApprovedBadge terms={ap.terms_days} date={ap.date} by={ap.by_name} testId={`ca-approved-${a.id}`} />}
      </td>
    </tr>
  );
}

function AppTable({ apps, slug, empty, testId }) {
  return (
    <div className="bg-white border border-[#e5e7eb] rounded-lg overflow-x-auto" data-testid={testId}>
      <table className="w-full text-sm"><tbody>
        {apps.length === 0 && <tr><td className="px-3 py-6 text-[#6b7280]" data-testid={`${testId}-empty`}>{empty}</td></tr>}
        {apps.map((a) => <AppRow key={a.id} a={a} slug={slug} />)}
      </tbody></table>
    </div>
  );
}

function NewApps({ apps, slug }) {
  const [f, setF] = useState("all");
  return (
    <div className="space-y-3">
      <div className="flex gap-2 flex-wrap" role="group" aria-label="Filter">
        {FILTERS.map(([k, l]) => <button key={k} className={`px-3 py-1 rounded-full text-sm border ${f === k ? "bg-[#0e1420] text-white border-[#0e1420]" : "border-[#cfd4dc]"}`} aria-pressed={f === k} onClick={() => setF(k)} data-testid={`ca-filter-${k}`}>{l}</button>)}
      </div>
      <AppTable apps={filterApps(apps, f)} slug={slug} empty="Nothing in this view." testId="ca-new-list" />
    </div>
  );
}

function HowItWorks() {
  return (
    <div className="space-y-3" data-testid="ca-how">
      <ol className="bg-white border border-[#e5e7eb] rounded-lg divide-y divide-[#f1f2f4]">
        {HOW_STEPS.map(([step, owner, todo], i) => (
          <li key={step} className="px-4 py-3 flex gap-4" data-testid={`ca-how-step-${i + 1}`}>
            <span className="w-6 h-6 rounded-full bg-[#0e1420] text-white text-xs grid place-items-center shrink-0">{i + 1}</span>
            <div><div className="font-semibold text-sm">{step}{owner && <span className="font-normal text-[#6b7280]"> · {owner}</span>}</div><div className="text-sm">{todo}</div></div>
          </li>
        ))}
      </ol>
      <p className="text-sm bg-[#fff8e6] border border-[#f5d98b] rounded-lg px-4 py-3" data-testid="ca-policy">{POLICY}</p>
    </div>
  );
}

function TabBar({ tab, slug, s }) {
  const badge = { new: s.newCount, legacy: s.legacy, active: s.active, completed: s.completed };
  return (
    <div className="flex gap-1 border-b border-[#e5e7eb] flex-wrap" role="tablist" data-testid="ca-tabs">
      {TABS.map(([k, l]) => (
        <Link key={k} role="tab" aria-selected={tab === k} to={`/${slug}/app/commercial-accounts/${k}`} data-testid={`ca-tab-${k}`}
          className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === k ? "border-[#0072bc] text-[#0072bc] font-semibold" : "border-transparent text-[#374151]"}`}>
          {l}{badge[k] != null && <span className="ml-1.5 text-xs px-1.5 rounded-full bg-[#eef1f6] text-[#374151]" data-testid={`ca-badge-${k}`}>{badge[k]}</span>}
        </Link>
      ))}
    </div>
  );
}

export default function CommercialAccounts() {
  const { slug, tab } = useParams();
  const navigate = useNavigate();
  const { scope, err } = useTermsScope();
  const [apps, setApps] = useState(null);
  const [register, setRegister] = useState(null);
  useEffect(() => {
    if (!scope) return;
    const params = { tenant_slug: scope.tenant_slug };
    api.get("/terms/applications", { params }).then(({ data }) => setApps(data.items)).catch(() => setApps([]));
    api.get("/terms/register", { params }).then(({ data }) => setRegister(data.items)).catch(() => setRegister([]));
  }, [scope]);
  const ready = scope && apps && register;
  const s = ready ? summarize(apps, register) : null;
  useEffect(() => { if (s && !tab) navigate(`/${slug}/app/commercial-accounts/${landingTab(s)}`, { replace: true }); }, [s, tab, slug, navigate]);
  if (err) return <div className="text-sm text-[#b91c1c]" data-testid="ca-error">{err}</div>;
  if (!ready || !tab) return null;
  return (
    <div className="space-y-4" data-testid="commercial-accounts-page">
      <h1 className="text-3xl font-semibold text-[#0e1420]">Commercial Accounts</h1>
      <Summary s={s} />
      <TabBar tab={tab} slug={slug} s={s} />
      {tab === "how" && <HowItWorks />}
      {tab === "new" && <NewApps apps={apps} slug={slug} />}
      {tab === "legacy" && <TermsLegacy />}
      {tab === "active" && <TermsRegister embedded defaultCohort="2027" />}
      {tab === "completed" && <AppTable apps={apps.filter((a) => a.progress?.completed)} slug={slug} empty="No completed applications." testId="ca-completed-list" />}
      {tab === "settings" && <TermsSettingsPanel slug={scope.tenant_slug} canEdit={scope.can_edit_settings} />}
    </div>
  );
}
